/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { resourceUpdated, selectResourceById } from "../slices/resourceCollection/resourceCollectionSlice";
import jsonEquals from "../functions/jsonEquals";
import DatatypeSelector from "../datatypeComponents/DatatypeSelector";
import StringEntry from "../datatypeComponents/StringEntry";
import ReferenceEntry, { DisplayFromFHIRReference, displayStringFromFHIRReference } from "../datatypeComponents/ReferenceEntry";
import CodeableConceptEntry, { simpleDisplayStringFromFHIRCodeableConcept } from "../datatypeComponents/CodeableConceptEntry";
import UriEntry, { DisplayFromFHIRUri } from "../datatypeComponents/UriEntry";
import { displayStringFromFHIRBoolean } from "../datatypeComponents/BooleanEntry";
import { displayStringFromFHIRQuantity } from "../datatypeComponents/QuantityEntry";
import UnsignedIntEntry from "../datatypeComponents/UnsignedIntEntry";
import ExpandToAddOrEdit from "./ExpandToAddOrEdit";
import { sectionCodeJustification, sectionCodeOutcomeDesirability, sectionCodePopulation, sectionCodeRelativeImportance } from "../dictionaries/CodeSystemLookup";
import { DisplayFromFHIRRelatesTo } from "../datatypeComponents/RelatesToEntry";

const DisplayFromFHIRRating = ({ rating, containedResourceDictionary = {} }) => {
    if (rating) {
        if (Array.isArray(rating) || typeof rating !== "object") {
            return <>[ERROR: Rating data not interpretable.]</>;
        }
        let ratingType = simpleDisplayStringFromFHIRCodeableConcept(rating.type) || "[Untyped]";
        let ratingClassifiers = "";
        if (Array.isArray(rating.classifier) && rating.classifier.length > 0) {
            ratingClassifiers = rating.classifier.map((classifier) => { return simpleDisplayStringFromFHIRCodeableConcept(classifier) }).join('; ');
        }
        let ratingQuantity = "";
        if (rating.quantity) {
            ratingQuantity = displayStringFromFHIRQuantity(rating.quantity) || "";
        }
        let ratingAuthor = "";
        if (Array.isArray(rating.author) && rating.author.length > 0) {
            ratingAuthor = rating.author.map((author) => { return displayStringFromFHIRReference(author) }).join("; ");
        }
        let ratingFreeToShare = displayStringFromFHIRBoolean(rating.freeToShare) || null;
        let ratingRelatesTo;
        if (rating.relatesTo?.[0]) {
            ratingRelatesTo = rating.relatesTo[0];
        }
        return <div>
            <p><b>{ratingType} ratings:</b></p>
            <div style={{ marginLeft: "24px" }}>
                {ratingClassifiers && <p>{ratingClassifiers}</p>}
                {ratingQuantity && <p>{ratingQuantity}</p>}
                {(!ratingClassifiers && !ratingQuantity) && <p>[No ratings.]</p>}
                {ratingAuthor && <p>Rated by: {ratingAuthor}</p>}
                {ratingRelatesTo && <div><DisplayFromFHIRRelatesTo relatesTo={ratingRelatesTo}
                    containedResourceDictionary={containedResourceDictionary} /></div>}
                {ratingFreeToShare === 'True' && <p>Free to share.</p>}
                {ratingFreeToShare === 'False' && <p>NOT free to share.</p>}
                {(Array.isArray(rating.component) && rating.component.length > 0) &&
                    rating.component.map((component, componentIndex) => {
                        return <div key={componentIndex}>
                            <DisplayFromFHIRRating rating={component}
                                containedResourceDictionary={containedResourceDictionary} />
                        </div>
                    })}
            </div>
        </div>
    } else {
        return <></>
    }
}

const OutcomeImportanceContentEntry = ({ elementName, fieldLabel, startingValue,
    dataEntryStyle, startCollapsed, foi, containedResourceDictionary = {} }) => {

    const dispatch = useDispatch();
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));

    const [outcomeImportanceContentState, setOutcomeImportanceContentState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let contentType = "";
        if (dataEntryStyle === "Outcome Desirability") {
            contentType = sectionCodeOutcomeDesirability;
        } else if (dataEntryStyle === "Relative Importance") {
            contentType = sectionCodeRelativeImportance;
        } else if (dataEntryStyle === "Population") {
            contentType = sectionCodePopulation;
        } else if (dataEntryStyle === "Justification") {
            contentType = sectionCodeJustification;
        }
        let startingOutcomeImportanceContent = {
            "summary": "", "type": contentType,
            "classifier": [], "classifierInstance": "", "quantity": "", "quantityValue": 0,
            "author": [], "path": [], "relatesTo": [], "relatesToInstanceReference": "",
            "relatesToJustificationReference": "", "freeToShare": true, "component": []
        };
        if (startingValue) {
            if (startingValue.extension) { startingOutcomeImportanceContent.extension = startingValue.extension; }
            if (startingValue.summary) { startingOutcomeImportanceContent.summary = startingValue.summary; }
            if (startingValue.type) { startingOutcomeImportanceContent.type = startingValue.type; }
            if (startingValue.classifier) {
                startingOutcomeImportanceContent.classifier = startingValue.classifier;
                startingOutcomeImportanceContent.classifierInstance = startingValue.classifier[0];
            }
            if (startingValue.quantity) {
                startingOutcomeImportanceContent.quantityValue = startingValue.quantity.value;
            }
            if (startingValue.author) { startingOutcomeImportanceContent.author = startingValue.author; }
            if (startingValue.relatesTo) {
                startingOutcomeImportanceContent.relatesTo = startingValue.relatesTo;
                startingOutcomeImportanceContent.relatesToInstanceReference = startingValue.relatesTo[0].targetReference;
                startingOutcomeImportanceContent.relatesToJustificationReference = startingValue.relatesTo[0].targetReference;
            }
            if (typeof startingValue.freeToShare === "boolean") { startingOutcomeImportanceContent.freeToShare = startingValue.freeToShare; }
        }
        setOutcomeImportanceContentState(JSON.parse(JSON.stringify(startingOutcomeImportanceContent)));
    }, []);

    useEffect(() => {
        if (outcomeImportanceContentState?.type && outcomeImportanceContentState.classifierInstance !== undefined && elementName === "content[0]") {
            const previousFinalValue = parentResourceDetail.revisedResourceJson.content?.[0]?.classifier?.[0];
            if ((previousFinalValue || outcomeImportanceContentState.classifierInstance)
                && !jsonEquals(previousFinalValue, outcomeImportanceContentState.classifierInstance)) {
                console.log("resourceUpdate for ArtifactAssessment.content[0].classifier[0]");
                let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
                let revisedResourceJson = newResourceDetail.revisedResourceJson;
                if (!revisedResourceJson.content) {
                    revisedResourceJson.content = [];
                }
                if (!revisedResourceJson.content[0]) {
                    revisedResourceJson.content.push({ "type": sectionCodeOutcomeDesirability });
                }
                revisedResourceJson.content[0].classifier = [outcomeImportanceContentState.classifierInstance];
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [outcomeImportanceContentState.classifierInstance]);

    useEffect(() => {
        if (outcomeImportanceContentState?.type && outcomeImportanceContentState.quantityValue !== undefined && elementName === "content[1]") {
            const previousFinalValue = parentResourceDetail.revisedResourceJson.content?.[1]?.quantity;
            if ((previousFinalValue || outcomeImportanceContentState.quantityValue) &&
                !jsonEquals(previousFinalValue, {
                    "value": outcomeImportanceContentState.quantityValue,
                    "unit": "%",
                    "system": "http://unitsofmeasure.org",
                    "code": "%"
                })) {
                console.log("resourceUpdate for ArtifactAssessment.content[1].quantity");
                let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
                let revisedResourceJson = newResourceDetail.revisedResourceJson;
                if (!revisedResourceJson.content) {
                    revisedResourceJson.content = [];
                }
                if (!revisedResourceJson.content[0]) {
                    revisedResourceJson.content.push({});
                }
                if (!revisedResourceJson.content[1]) {
                    revisedResourceJson.content.push({ "type": sectionCodeRelativeImportance });
                }
                revisedResourceJson.content[1].quantity = {
                    "value": outcomeImportanceContentState.quantityValue,
                    "unit": "%",
                    "system": "http://unitsofmeasure.org",
                    "code": "%"
                };
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [outcomeImportanceContentState.quantityValue]);

    useEffect(() => {
        if (outcomeImportanceContentState?.type && outcomeImportanceContentState.relatesToInstanceReference !== undefined && elementName === "content[2]") {
            const previousFinalValue = parentResourceDetail.revisedResourceJson.content?.[2]?.relatesTo?.[0]?.targetReference;
            if ((previousFinalValue || outcomeImportanceContentState.relatesToInstanceReference) &&
                !jsonEquals(previousFinalValue, outcomeImportanceContentState.relatesToInstanceReference)) {
                console.log("resourceUpdate for ArtifactAssessment.content[2].relatesTo[0].targetReference");
                let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
                let revisedResourceJson = newResourceDetail.revisedResourceJson;
                if (!revisedResourceJson.content) {
                    revisedResourceJson.content = [];
                }
                if (!revisedResourceJson.content[0]) {
                    revisedResourceJson.content.push({});
                }
                if (!revisedResourceJson.content[1]) {
                    revisedResourceJson.content.push({});
                }
                if (!revisedResourceJson.content[2]) {
                    revisedResourceJson.content.push({ "type": sectionCodePopulation });
                }
                revisedResourceJson.content[2].relatesTo = [{
                    "type": {
                        "coding": [
                            {
                                "system": "http://terminology.hl7.org/CodeSystem/artifact-relationship-type",
                                "code": "supports",
                                "display": "Supports"
                            }
                        ]
                    },
                    "targetReference": outcomeImportanceContentState.relatesToInstanceReference
                }];
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [outcomeImportanceContentState.relatesToInstanceReference]);

    useEffect(() => {
        if (outcomeImportanceContentState?.type && outcomeImportanceContentState.relatesToJustificationReference !== undefined && elementName === "content[3]") {
            const previousFinalValue = parentResourceDetail.revisedResourceJson.content?.[3]?.relatesTo?.[0]?.targetReference;
            if ((previousFinalValue || outcomeImportanceContentState.relatesToJustificationReference) &&
                !jsonEquals(previousFinalValue, outcomeImportanceContentState.relatesToJustificationReference)) {
                console.log("resourceUpdate for ArtifactAssessment.content[3].relatesTo[0].targetReference");
                let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
                let revisedResourceJson = newResourceDetail.revisedResourceJson;
                if (!revisedResourceJson.content) {
                    revisedResourceJson.content = [];
                }
                if (!revisedResourceJson.content[0]) {
                    revisedResourceJson.content.push({});
                }
                if (!revisedResourceJson.content[1]) {
                    revisedResourceJson.content.push({});
                }
                if (!revisedResourceJson.content[2]) {
                    revisedResourceJson.content.push({ "type": sectionCodePopulation });
                }
                if (!revisedResourceJson.content[3]) {
                    revisedResourceJson.content.push({ "type": sectionCodeJustification });
                }
                revisedResourceJson.content[3].relatesTo = [{
                    "type": {
                        "coding": [
                            {
                                "system": "http://terminology.hl7.org/CodeSystem/artifact-relationship-type",
                                "code": "justification",
                                "display": "Justification"
                            }
                        ]
                    },
                    "targetReference": outcomeImportanceContentState.relatesToJustificationReference
                }];
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [outcomeImportanceContentState.relatesToJustificationReference]);

    if (startCollapsedState) {
        return <>
            <div>
                <b>{fieldLabel}: </b>
                {startingValue && <DisplayFromFHIRRating rating={startingValue}
                    containedResourceDictionary={containedResourceDictionary} />}
                &nbsp;&nbsp;
                <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
            </div>
        </>
    } else if (dataEntryStyle === "Outcome Desirability") {
        return <>{outcomeImportanceContentState.classifierInstance !== undefined && <>
            <p style={{ marginBottom: "0px" }}><b>Outcome Desirability: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <CodeableConceptEntry elementName='classifierInstance' fieldLabel='Outcome Desirability Classification'
                    startingValue={outcomeImportanceContentState.classifierInstance || null}
                    setResourceState={setOutcomeImportanceContentState}
                    codeableConceptLevelValueSet={[ //Outcome Desirability
                        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "desirable", display: "Desirable" }] },
                        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "undesirable", display: "Undesirable" }] }
                    ]} />
            </div>
        </>}
        </>
    } else if (dataEntryStyle === "Relative Importance") {
        return <>{outcomeImportanceContentState.quantityValue !== undefined && <>
            <p style={{ marginBottom: "0px" }}><b>Relative Importance: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <UnsignedIntEntry elementName='quantityValue' fieldLabel='Relative Importance (%)'
                    startingValue={outcomeImportanceContentState.quantityValue || 0}
                    setResourceState={setOutcomeImportanceContentState} />
            </div>
        </>}
        </>
    } else if (dataEntryStyle === "Population") {
        return <>{outcomeImportanceContentState.relatesToInstanceReference !== undefined && <>
            <p style={{ marginBottom: "0px" }}><b>For Whom: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <ReferenceEntry elementName='relatesToInstanceReference' fieldLabel='Population Reference'
                    startingValue={outcomeImportanceContentState.relatesToInstanceReference}
                    containedResourceDictionary={containedResourceDictionary}
                    referencedResourceTypes={["Group", "Patient", "Practitioner",
                        "PractitionerRole", "RelatedPerson", "CareTeam", "Device",
                        "HealthcareService", "Organization"]}
                    enableCreation={true} startCollapsed={true}
                    setResourceState={setOutcomeImportanceContentState} />
            </div>
        </>}
        </>
    } else if (dataEntryStyle === "Justification") {
        return <>{outcomeImportanceContentState.relatesToJustificationReference !== undefined && <>
            <p style={{ marginBottom: "0px" }}><b>Justification: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <ReferenceEntry elementName='relatesToJustificationReference' fieldLabel='Justification Reference'
                    startingValue={outcomeImportanceContentState.relatesToJustificationReference}
                    containedResourceDictionary={containedResourceDictionary}
                    referencedResourceTypes={["Citation", "Composition", "Evidence"]}
                    enableCreation={true} startCollapsed={true}
                    setResourceState={setOutcomeImportanceContentState} />
            </div>
        </>}
        </>
    } else {
        return <>
            <p>Something is not right.</p>
        </>
    }
}

const OutcomeImportanceEdit = ({ foi }) => {

    const dispatch = useDispatch();
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));

    const [editResourceState, setEditResourceState] = useState({});
    const [resourceLoaded, setResourceLoaded] = useState(false);
    const [artifactReferenceEditState, setArtifactReferenceEditState] = useState(false);
    const [artifactReferenceDatatypeState, setArtifactReferenceDatatypeState] = useState('none');
    const [contentArrayState, setContentArrayState] = useState({});
    const [containedResourceDictionary, setContainedResourceDictionary] = useState({});

    useEffect(() => {
        if (!resourceLoaded && parentResourceDetail?.revisedResourceJson) {
            const revisedResourceJson = parentResourceDetail?.revisedResourceJson;
            if (Array.isArray(revisedResourceJson.contained) && revisedResourceJson.contained.length > 0) {
                let containedResourceDictionary = {};
                for (const item of revisedResourceJson.contained) {
                    const key = "#" + item.id;
                    containedResourceDictionary[key] = item;
                }
                setContainedResourceDictionary(containedResourceDictionary);
            }
            let resourceJsonContent = JSON.parse(JSON.stringify(revisedResourceJson?.content));
            if (!resourceJsonContent || !Array.isArray(resourceJsonContent) || resourceJsonContent.length === 0) {
                resourceJsonContent = [
                    {
                        "type": sectionCodeOutcomeDesirability
                    }
                ];
            }
            if (resourceJsonContent.length === 1) {
                resourceJsonContent.push({
                    "type": sectionCodeRelativeImportance
                });
            }
            if (resourceJsonContent.length === 2) {
                resourceJsonContent.push({
                    "type": sectionCodePopulation
                });
            }
            if (resourceJsonContent.length === 3) {
                resourceJsonContent.push({
                    "type": sectionCodeJustification
                });
            }
            setContentArrayState({
                "content[0]": resourceJsonContent[0],
                "content[1]": resourceJsonContent[1],
                "content[2]": resourceJsonContent[2],
                "content[3]": resourceJsonContent[3]
            });
            let startingArtifactReferenceDatatype;
            if (revisedResourceJson.artifactUri) {
                startingArtifactReferenceDatatype = 'uri';
            } else if (revisedResourceJson.artifactCanonical) {
                startingArtifactReferenceDatatype = 'canonical';
            } else if (revisedResourceJson.artifactReference) {
                startingArtifactReferenceDatatype = 'Reference';
            } else {
                startingArtifactReferenceDatatype = 'none';
            }
            setArtifactReferenceDatatypeState(startingArtifactReferenceDatatype);
            setEditResourceState({
                title: revisedResourceJson?.title,
                artifactReference: revisedResourceJson?.artifactReference && JSON.parse(JSON.stringify(revisedResourceJson?.artifactReference)),
                artifactCanonical: revisedResourceJson?.artifactCanonical,
                artifactUri: revisedResourceJson?.artifactUri,
                relatesTo: revisedResourceJson?.relatesTo && JSON.parse(JSON.stringify(revisedResourceJson?.relatesTo)),
                content: resourceJsonContent
            });
            setResourceLoaded(true);
        }
    }, [resourceLoaded]);

    useEffect(() => {
        if (editResourceState?.title !== undefined && !jsonEquals(parentResourceDetail.revisedResourceJson.title, editResourceState.title)) {
            console.log("resourceUpdate for ArtifactAssessment.title");
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            newResourceDetail.revisedResourceJson.title = editResourceState.title;
            newResourceDetail.displayTitle = editResourceState.title;
            dispatch(resourceUpdated(newResourceDetail));
        }
    }, [editResourceState.title]);

    useEffect(() => {
        if (editResourceState?.artifactReference !== undefined &&
            (editResourceState.artifactReference || parentResourceDetail.revisedResourceJson.artifactReference) &&
            !jsonEquals(editResourceState.artifactReference, parentResourceDetail.revisedResourceJson.artifactReference || null)) {
            console.log("resourceUpdate for ArtifactAssessment.artifactReference");
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            newResourceDetail.revisedResourceJson.artifactReference = editResourceState.artifactReference;
            dispatch(resourceUpdated(newResourceDetail));
        }
    }, [editResourceState.artifactReference]);

    useEffect(() => {
        if (editResourceState?.artifactCanonical !== undefined) {
            const previousFinalValue = parentResourceDetail.revisedResourceJson.artifactCanonical || null;
            if ((previousFinalValue || editResourceState.artifactCanonical) && previousFinalValue !== editResourceState.artifactCanonical) {
                console.log("resourceUpdate for ArtifactAssessment.artifactCanonical");
                let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
                newResourceDetail.revisedResourceJson.artifactCanonical = editResourceState.artifactCanonical;
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [editResourceState.artifactCanonical]);

    useEffect(() => {
        if (editResourceState?.artifactUri !== undefined) {
            let previousFinalValue = parentResourceDetail.revisedResourceJson.artifactUri || null;
            if ((previousFinalValue || editResourceState.artifactUri) && previousFinalValue !== editResourceState.artifactUri) {
                console.log("resourceUpdate for ArtifactAssessment.artifactUri");
                let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
                newResourceDetail.revisedResourceJson.artifactUri = editResourceState.artifactUri;
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [editResourceState.artifactUri]);

    useEffect((() => {
        if (artifactReferenceDatatypeState === 'uri') {
            setEditResourceState(prevState => { return { ...prevState, 'artifactReference': null, 'artifactCanonical': null } })
        }
        if (artifactReferenceDatatypeState === 'Reference') {
            setEditResourceState(prevState => { return { ...prevState, 'artifactUri': null, 'artifactCanonical': null } })
        }
        if (artifactReferenceDatatypeState === 'canonical') {
            setEditResourceState(prevState => { return { ...prevState, 'artifactReference': null, 'artifactUri': null } })
        }
    }), [artifactReferenceDatatypeState]);

    return <div style={{ marginTop: "12px" }}>
        <h3 id="summary">Summary</h3>
        <div style={{ marginLeft: "24px" }}>
            <StringEntry elementName='title' fieldLabel='Title'
                startingValue={editResourceState.title}
                setResourceState={setEditResourceState} />
        </div>
        <div>
            <div><b>The Outcome Being Rated: </b>
                <DisplayFromFHIRReference reference={editResourceState.artifactReference}
                    containedResourceJson={editResourceState.artifactReference?.reference && containedResourceDictionary[editResourceState.artifactReference.reference]} />
                <DisplayFromFHIRUri uri={editResourceState.artifactUri || editResourceState.artifactCanonical} />
                <span className={"unselectable"} style={{ marginLeft: "24px", cursor: "pointer" }}
                    onClick={() => { setArtifactReferenceEditState(!artifactReferenceEditState) }}>
                    <i>Change the Outcome (EvidenceVariable Resource) Being Rated</i>&nbsp;&nbsp;
                    {artifactReferenceEditState ? <>▼</> : <>►</>}
                </span>
            </div>
            {artifactReferenceEditState &&
                <div style={{ whiteSpace: "pre-wrap", paddingLeft: "24px" }}>
                    <DatatypeSelector elementXName='artifact[x]' allowedDatatypes={['Reference', 'canonical', 'uri']}
                        datatypeState={artifactReferenceDatatypeState} setDatatypeState={setArtifactReferenceDatatypeState} />
                    {(artifactReferenceDatatypeState === 'uri') &&
                        <UriEntry elementName='artifactUri'
                            fieldLabel='The Outcome Being Rated (by URL)'
                            startingValue={editResourceState.artifactUri || null}
                            setResourceState={setEditResourceState} />}
                    {(artifactReferenceDatatypeState === 'Reference') &&
                        <ReferenceEntry elementName='artifactReference'
                            containedResourceDictionary={containedResourceDictionary}
                            fieldLabel='The Outcome Being Rated' startingResourceType="EvidenceVariable"
                            startingValue={editResourceState.artifactReference || null}
                            setResourceState={setEditResourceState} />}
                    {(artifactReferenceDatatypeState === 'canonical') &&
                        <UriEntry elementName='artifactCanonical'
                            fieldLabel='The Outcome Being Rated (by Canonical URI)'
                            startingValue={editResourceState.artifactCanonical || null}
                            setResourceState={setEditResourceState} />}
                    <br />
                </div>
            }
            <h3 id="outcome-desirability">Outcome Desirability</h3>
            <div style={{ marginLeft: "24px" }}>
                {contentArrayState["content[0]"] ?
                    <OutcomeImportanceContentEntry elementName='content[0]'
                        containedResourceDictionary={containedResourceDictionary}
                        fieldLabel='Outcome Desirability' foi={foi}
                        startingValue={contentArrayState["content[0]"] || null}
                        dataEntryStyle='Outcome Desirability' startCollapsed />
                    :
                    <p>Loading ...</p>}
                <br />
                <br />
            </div>
            <br />
        </div>
        <div>
            <h3 id="relative-importance">Relative Importance</h3>
            <div style={{ marginLeft: "24px" }}>
                {contentArrayState["content[1]"] ?
                    <OutcomeImportanceContentEntry elementName='content[1]' fieldLabel='Relative Importance'
                        containedResourceDictionary={containedResourceDictionary}
                        startingValue={contentArrayState["content[1]"] || null} foi={foi}
                        dataEntryStyle='Relative Importance' startCollapsed />
                    :
                    <p>Loading ...</p>}
            </div>
            <br />
        </div>
        <div>
            <h3 id="for-whom">For Whom</h3>
            <div style={{ marginLeft: "24px" }}>
                {contentArrayState["content[2]"] ?
                    <OutcomeImportanceContentEntry elementName='content[2]' fieldLabel='For Whom'
                        containedResourceDictionary={containedResourceDictionary}
                        startingValue={contentArrayState["content[2]"] || null} foi={foi}
                        dataEntryStyle='Population' startCollapsed />
                    :
                    <p>Loading ...</p>}
            </div>
            <br />
        </div>
        <div>
            <h3 id="justification">Justification</h3>
            <div style={{ marginLeft: "24px" }}>
                {contentArrayState["content[3]"] ?
                    <OutcomeImportanceContentEntry elementName='content[3]' fieldLabel='Justification'
                        containedResourceDictionary={containedResourceDictionary}
                        startingValue={contentArrayState["content[3]"] || null} foi={foi}
                        dataEntryStyle='Justification' startCollapsed />
                    :
                    <p>Loading ...</p>}
            </div>
            <br />
        </div>
    </div>
};

export default OutcomeImportanceEdit;